/**
 * Produktdata for utstyr (VIB-2918): typer for forslaget fra equipment-enrich,
 * status per modell på /utstyr/produktdata og batch-kjøringen («Hent for alle»).
 *
 * Forslagets form speiler `Candidates` i supabase/functions/_shared/equipment-enrich.ts.
 */

export type MediaKind = "image" | "manual" | "datasheet" | "document";
export type MediaSource = "upload" | "manufacturer" | "web";

export interface EnrichmentCandidates {
  product_url: string | null;
  source_pages: { url: string; title: string; official: boolean }[];
  images: { url: string; host: string; source: Exclude<MediaSource, "upload"> }[];
  documents: {
    url: string;
    title: string;
    host: string;
    kind: Exclude<MediaKind, "image">;
    source: Exclude<MediaSource, "upload">;
    thirdPartyCopy: boolean;
  }[];
  specs: { label: string; value: string }[];
  description: string | null;
  part_no: string | null;
  found_model: string | null;
  warnings: string[];
}

export interface CandidateSelection {
  images: string[];
  documents: string[];
  includeSpecs: boolean;
  specs: { label: string; value: string }[];
  includeDescription: boolean;
}

/** Forhåndsvalg: første bilde, og produsentens manualer og datablad. */
export function defaultSelection(c: EnrichmentCandidates): CandidateSelection {
  return {
    images: c.images.slice(0, 1).map((i) => i.url),
    documents: c.documents.filter((d) => d.source === "manufacturer" && d.kind !== "document").map((d) => d.url),
    includeSpecs: c.specs.length > 0,
    specs: c.specs,
    includeDescription: !!c.description,
  };
}

export type EnrichmentRunStatus = "running" | "ready" | "applied" | "failed" | "discarded";

export interface EnrichmentRun {
  id: string;
  product_id: string;
  status: EnrichmentRunStatus;
  candidates: EnrichmentCandidates | null;
  error: string | null;
  credits_used: number | null;
  created_at: string;
}

export const MEDIA_KIND_LABELS: Record<MediaKind, string> = {
  image: "Bilde",
  manual: "Manual",
  datasheet: "Datablad",
  document: "Dokument",
};

export type ProductDataStatus = "saved" | "review" | "running" | "failed" | "missing";

export const PRODUCT_DATA_STATUS_LABELS: Record<ProductDataStatus, string> = {
  saved: "Lagret",
  review: "Venter på gjennomsyn",
  running: "Henter …",
  failed: "Feilet",
  missing: "Mangler data",
};

/** Rekkefølgen på /utstyr/produktdata: det som venter på en person først. */
export const PRODUCT_DATA_STATUS_ORDER: Record<ProductDataStatus, number> = { review: 0, running: 1, failed: 2, saved: 3, missing: 4 };

/**
 * En henting tar under 30 s. Står en kjøring som «running» lenger, døde
 * workeren før den fikk skrevet resultatet, og kjøringen regnes som feilet.
 */
export const STALE_RUN_MS = 3 * 60 * 1000;

export function isRunInProgress(run: Pick<EnrichmentRun, "status" | "created_at"> | null | undefined, now = Date.now()): boolean {
  return run?.status === "running" && now - new Date(run.created_at).getTime() < STALE_RUN_MS;
}

/**
 * Siste kjøring per modell. Velges på `created_at`, ikke på rekkefølgen fra
 * spørringen: `fetchAllPaginated` sorterer på id først.
 */
export function latestRunByProduct<R extends Pick<EnrichmentRun, "product_id" | "created_at">>(runs: readonly R[]): Map<string, R> {
  const latest = new Map<string, R>();
  for (const r of runs) {
    const cur = latest.get(r.product_id);
    if (!cur || Date.parse(r.created_at) > Date.parse(cur.created_at)) latest.set(r.product_id, r);
  }
  return latest;
}

/**
 * Status for én modell. Et forslag som venter på gjennomsyn går foran «Lagret»,
 * så en ny henting for en modell som har data vises i køen.
 */
export function productDataStatus(
  enrichedAt: string | null,
  latestRun: Pick<EnrichmentRun, "status" | "created_at"> | null,
  now = Date.now(),
): ProductDataStatus {
  if (latestRun?.status === "ready") return "review";
  if (isRunInProgress(latestRun, now)) return "running";
  if (enrichedAt) return "saved";
  if (latestRun?.status === "failed" || latestRun?.status === "running") return "failed";
  return "missing";
}

/**
 * Kjører `task` for hver modell, høyst `limit` samtidig, og stopper å starte
 * nye når `shouldStop()` blir sann (stoppknappen, eller 402 tom for credits).
 * Påbegynte kall fullføres. Returnerer antall som ble startet.
 */
export async function runLimited<T>(
  items: readonly T[],
  limit: number,
  task: (item: T) => Promise<void>,
  shouldStop: () => boolean,
): Promise<number> {
  let next = 0;
  let started = 0;
  const worker = async () => {
    while (next < items.length && !shouldStop()) {
      const item = items[next++];
      started++;
      await task(item);
    }
  };
  await Promise.all(Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, worker));
  return started;
}
